/**
 * ButterGirls wishlist — localStorage only, no account/app required.
 * Exposes window.ButterGirlsWishlist: getWishlist, isInWishlist, addToWishlist, removeFromWishlist, toggleWishlist.
 * Dispatches a "wishlist:updated" CustomEvent (detail.handles) on document on every change.
 * Also wires up any [data-wishlist-toggle] button and [data-wishlist-count] badge found on the page.
 */
(() => {
  const STORAGE_KEY = 'buttergirls_wishlist';

  function readWishlist() {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function writeWishlist(handles) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { handles } }));
    return handles;
  }

  function getWishlist() {
    return readWishlist();
  }

  function isInWishlist(handle) {
    return readWishlist().includes(handle);
  }

  function addToWishlist(handle) {
    const handles = readWishlist();
    if (!handles.includes(handle)) {
      handles.push(handle);
      writeWishlist(handles);
    }
    return handles;
  }

  function removeFromWishlist(handle) {
    return writeWishlist(readWishlist().filter((existing) => existing !== handle));
  }

  function toggleWishlist(handle) {
    const active = !isInWishlist(handle);
    active ? addToWishlist(handle) : removeFromWishlist(handle);
    return active;
  }

  window.ButterGirlsWishlist = { getWishlist, isInWishlist, addToWishlist, removeFromWishlist, toggleWishlist };

  // --------------------------------------------------------------------------------------------------------------
  // UI wiring: heart buttons (product cards, product page, wishlist page) + header badge count
  // --------------------------------------------------------------------------------------------------------------

  function syncButton(button) {
    const handle = button.getAttribute('data-product-handle');
    const active = isInWishlist(handle);
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', String(active));
    button.setAttribute('aria-label', active ? 'Retirer des favoris' : 'Ajouter aux favoris');
  }

  function syncAllButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(syncButton);
  }

  function syncCountBadges() {
    const count = getWishlist().length;
    document.querySelectorAll('[data-wishlist-count]').forEach((badge) => {
      badge.textContent = String(count);
      badge.classList.toggle('opacity-0', count === 0);
    });
  }

  function sync() {
    syncAllButtons();
    syncCountBadges();
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;

    event.preventDefault();
    const handle = button.getAttribute('data-product-handle');
    if (handle) toggleWishlist(handle);
  });

  document.addEventListener('wishlist:updated', sync);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }

  // --------------------------------------------------------------------------------------------------------------
  // Wishlist drawer content: fetches each product via /products/{handle}.js and renders it using the theme's own
  // "horizontal product" markup/classes (same component the cart drawer uses for its recommendations), so it's
  // visually consistent with the rest of the site.
  // --------------------------------------------------------------------------------------------------------------

  const HEART_ICON = '<svg role="presentation" fill="none" focusable="false" stroke-width="1.5" width="18" height="18" viewBox="0 0 24 24"><path d="M12 5.457C6.823-.895 1.358 3.619 1.286 8.484c0 7.24 8.665 13.185 10.714 13.185 2.049 0 10.714-5.946 10.714-13.187C22.642 3.617 17.177-.895 12 5.457Z" fill="currentColor" fill-opacity="1" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  function withWidth(url, width) {
    if (!url) return url;
    return url.includes('?') ? `${url}&width=${width}` : `${url}?width=${width}`;
  }

  function formatMoney(cents, currency) {
    const amount = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', { style: 'currency', currency }).format(amount);
    } catch (error) {
      return `${amount.toFixed(2)} ${currency}`;
    }
  }

  function renderWishlistDrawerCard(product, currency) {
    const image = product.featured_image || (product.images && product.images[0]) || null;
    const variant = (product.variants || []).find((v) => v.available) || (product.variants || [])[0];
    const price = formatMoney(product.price, currency);
    const url = `/products/${escapeHtml(product.handle)}`;

    return `
      <div class="horizontal-product" data-wishlist-drawer-item>
        ${image ? `<a href="${url}" data-instant><img class="horizontal-product__image rounded-xs" src="${escapeHtml(withWidth(image, 160))}" alt="${escapeHtml(product.title)}" loading="lazy" width="80" height="80"></a>` : ''}
        <div class="horizontal-product__info">
          <div class="v-stack gap-0.5">
            <a href="${url}" data-instant class="text-sm bold">${escapeHtml(product.title)}</a>
            <span class="text-sm">${price}</span>
          </div>

          <div class="horizontal-product__cta h-stack gap-2 items-center">
            ${variant ? `
              <form is="product-form" class="contents">
                <input type="hidden" name="id" value="${variant.id}">
                <input type="hidden" name="quantity" value="1">
                <button type="submit" class="button button--sm button--subdued">${'Ajouter au panier'}</button>
              </form>
            ` : ''}
            <button type="button" class="wishlist-button" data-wishlist-toggle data-product-handle="${escapeHtml(product.handle)}" aria-pressed="true" aria-label="Retirer des favoris">
              ${HEART_ICON}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  async function renderWishlistDrawer() {
    const drawer = document.getElementById('wishlist-drawer');
    if (!drawer) return;

    const body = drawer.querySelector('[data-wishlist-drawer-body]');
    if (!body) return;

    const currency = drawer.getAttribute('data-currency') || 'EUR';
    const handles = getWishlist();

    if (handles.length === 0) {
      body.innerHTML = `
        <div class="empty-state align-self-center">
          <div class="empty-state__icon-wrapper">
            ${HEART_ICON}
          </div>
          <div class="prose">
            <p class="h5">Votre liste d’envies est vide</p>
            <p>Cliquez sur le cœur d’un produit pour l’ajouter ici.</p>
          </div>
        </div>
      `;
      return;
    }

    body.innerHTML = '<p class="text-subdued">Chargement…</p>';

    const products = await Promise.all(
      handles.map((handle) =>
        fetch(`/products/${handle}.js`)
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      )
    );

    const validHandles = [];
    let markup = '';

    products.forEach((product, index) => {
      if (product) {
        markup += renderWishlistDrawerCard(product, currency);
        validHandles.push(handles[index]);
      }
    });

    if (validHandles.length !== handles.length) {
      handles.forEach((handle) => {
        if (!validHandles.includes(handle)) removeFromWishlist(handle);
      });
    }

    body.innerHTML = markup || '<p class="text-subdued">Votre liste d’envies est vide</p>';
  }

  document.addEventListener('dialog:after-show', (event) => {
    if (event.target && event.target.id === 'wishlist-drawer') renderWishlistDrawer();
  });

  document.addEventListener('wishlist:updated', () => {
    const drawer = document.getElementById('wishlist-drawer');
    if (drawer && drawer.hasAttribute('open')) renderWishlistDrawer();
  });

  // When adding to cart from within the wishlist drawer, close it so the cart drawer (which auto-opens on
  // "cart:change") takes over cleanly, instead of the two drawers stacking on top of each other.
  document.addEventListener('cart:change', () => {
    const drawer = document.getElementById('wishlist-drawer');
    if (drawer && drawer.hasAttribute('open') && typeof drawer.hide === 'function') {
      drawer.hide();
    }
  });

  // Guarantee a single, consistent result on every add-to-cart, anywhere on the site: close any other panel
  // (quick-buy variant picker, cart notification popover) that may have opened instead, and make sure the real
  // cart drawer is the one showing.
  document.addEventListener('cart:change', () => {
    document.querySelectorAll('quick-buy-drawer[open], cart-notification-drawer[open]').forEach((panel) => {
      if (typeof panel.hide === 'function') panel.hide();
    });

    const cartDrawer = document.getElementById('cart-drawer');
    if (cartDrawer && !cartDrawer.hasAttribute('open') && typeof cartDrawer.show === 'function') {
      cartDrawer.show();
    }
  });
})();
